<script lang="ts" setup>
import SessionToc from '@/vue/organisms/session-toc.vue';
import ContentTitleHeader from '@/vue/molecules/content-title-header.vue';
import {useInterventionStore} from '@/ts/store/intervention-store';
import {useRouterSectionParams, getVueLink} from '@/ts/composables/stateful/use-route-params';
import {usePageId} from '@/ts/composables/stateful/use-page';
import SessionIntroduction from '@/vue/organisms/session-introduction.vue';
import {computed, ref, watchEffect} from 'vue';
import SessionCmsContent from '@/vue/organisms/session-cms-content.vue';
import {useSessionId} from '@/ts/composables/stateful/use-session';
import {SessionState, PageState} from '@/ts/types/dto/interventions-dto';
import SessionUnlocks from '@/vue/molecules/session-summary/session-unlocks.vue';
import HomeworkCard from '@/vue/molecules/session-summary/homework-card.vue';
import HomeworkModal from '@/vue/organisms/homework-modal.vue';
import AllianzBrandingFooter from '@/vue/molecules/allianz-branding-footer.vue';
import CdcDppEligibilityDialog from '@/vue/organisms/cdc-dpp-eligibility-dialog.vue';
import CdcDppFallbackDialog from '@/vue/organisms/cdc-dpp-fallback-dialog.vue';
import InsuranceVerificationDialog from '@/vue/organisms/insurance-verification-dialog.vue';
import {useRouter} from 'vue-router';
import {pagesPath} from '@/ts/router/pages-path';
import {useAllianzBranding} from '@/ts/composables/stateful/use-allianz-branding';
import {useCdcDppEligibility} from '@/ts/composables/stateful/use-cdc-dpp-eligibility';

const intervention = useInterventionStore();
const router = useRouter();
const {isAllianzUser} = useAllianzBranding();

const {sid, pid, iid} = useRouterSectionParams();
const {page} = usePageId(sid, pid);

const {inProgress: isSessionInProgress} = useSessionId(sid);

const isFirstPageInSession = computed(() => {
  return pid.value === 1;
});

const sessionIntroReady = ref<boolean>(false);

const renderFollowUpContent = computed((): boolean => {
  return !isFirstPageInSession.value || sessionIntroReady.value;
});

const {session} = useSessionId(sid);

const currentSessionHomework = computed(() => session.value?.homework[0] ?? null);
const hasHomework = computed(() => Boolean(currentSessionHomework.value?.pages[0]));
const homeworkOpen = ref(false);

const {
  showEligibilityDialog,
  showInsuranceDialog,
  eligibilityResult,
  eligibleVariant,
  fallbackMode,
  grantingYear,
  onEligibilityContinue,
  onEligibilityExit,
  onEligibilityCheckInsurance,
  onInsuranceFunded,
  onInsuranceSkipped,
  onInsuranceBack,
  onFallbackGrantYear,
  onFallbackSubscriberContinue,
  onFallbackUpgrade,
  onFallbackDeclineToOffer,
  onFallbackContinue,
  onFallbackBack,
  onFallbackExit,
} = useCdcDppEligibility();

// The requested page index (1-based) resolves to a real page in this session.
const pageExists = computed((): boolean =>
  Boolean(session.value) && pid.value >= 1 && pid.value <= (session.value?.pages.length ?? 0));

// A session the user is allowed to open. Locked/future sessions are reachable by URL
// manipulation but must not render (IPD-2751). We only gate on LOCK here: a not-joined
// intervention is handled by the enrollment flow + BE (IPD-2752), and gating NOT_JOINED
// would wrongly redirect during the enroll→first-session transition (the store is briefly
// not-joined before the enrolled state lands).
const isSessionAccessible = computed((): boolean =>
  Boolean(session.value) && session.value!.state !== SessionState.LOCK);

// Guard against accessing future/locked or non-existent sessions via URL manipulation.
// Runs once the intervention has loaded; ordering matters (unknown → 404, locked → landing).
watchEffect(() => {
  if (!intervention.value) {
    return; // intervention still loading — nothing to validate yet
  }
  if (!session.value) {
    // Unknown session id (not part of this intervention) → error page.
    void router.replace(pagesPath.main.notFound);
    return;
  }
  if (!isSessionAccessible.value) {
    // Locked/future session → back to the intervention landing.
    void router.replace(getVueLink(pagesPath.intervention.interventionLanding, {iid: iid.value}));
    return;
  }
  if (!pageExists.value) {
    // Valid, accessible session but the page index is out of range → error page.
    void router.replace(pagesPath.main.notFound);
    return;
  }
  if (session.value.pages[pid.value - 1].state === PageState.LOCK) {
    // Future/locked page within an accessible session, reached by jumping ahead via URL
    // (IPD-2776). Send the user back to their current page (first unlocked one); completed
    // pages stay reachable for review, only LOCK is gated. Fallback to the landing if there
    // is no unlocked page (unexpected — completed sessions are handled by the watcher below).
    const currentPageIndex = session.value.pages.findIndex(p => p.state === PageState.UNLOCK);
    void router.replace(currentPageIndex >= 0
      ? getVueLink(pagesPath.session.sessionPage, {iid: iid.value, sid: sid.value, pid: currentPageIndex + 1})
      : getVueLink(pagesPath.intervention.interventionLanding, {iid: iid.value}));
  }
});

// Redirect to completed page if session is completed
watchEffect(() => {
  if (session.value && intervention.value) {
    // Check if session is completed OR all pages are completed
    const allPagesCompleted = session.value.pages?.length > 0 &&
      session.value.pages.every(p => p.state === PageState.COMPLETED);

    if (session.value.state === SessionState.COMPLETED || allPagesCompleted) {
      const completedPageUrl = getVueLink(pagesPath.session.sessionCompletedPage, {
        iid: iid.value,
        sid: sid.value,
      });
      void router.replace(completedPageUrl);
    }
  }
});

watchEffect(() => {
  if (isFirstPageInSession.value) {
    sessionIntroReady.value = false;
  }
});

</script>

<template>
  <div v-if="intervention && session && isSessionAccessible" class="intervention-session-page">
    <div class="toc-container">
      <session-toc/>
    </div>
    <div class="content-container">
      <div class="content-inner">
        <template v-if="page">
          <content-title-header
            class="title-header"
            :title="page.name"
            :badge-pill-txt="isSessionInProgress ? $t('current') : ''"
            :badge-zeppelin-txt="$t('page_n', {n: pid})"
            :image-id="page.image || intervention.image"
            :down-sized="false"
          />
          <session-introduction v-if="isFirstPageInSession" @ready="sessionIntroReady = true"/>
          <template v-if="renderFollowUpContent">
            <div v-if="isFirstPageInSession && hasHomework" class="homework-section">
              <h2 class="homework-heading">
                {{ $t('homework_from_last_session') }}
              </h2>
              <homework-card
                :homework="currentSessionHomework!"
                variant="detail"
                @open-homework="homeworkOpen = true"
              />
              <homework-modal v-if="homeworkOpen" v-model="homeworkOpen" :homework-session-id="sid"/>
            </div>
            <div>
              <session-unlocks :session="session"/>
            </div>
            <session-cms-content v-if="session?.state === SessionState.UNLOCK"/>
          </template>
        </template>
      </div>
      <allianz-branding-footer v-if="isAllianzUser"/>
    </div>

    <cdc-dpp-eligibility-dialog
      v-if="eligibilityResult !== null"
      v-model="showEligibilityDialog"
      :eligible="eligibilityResult"
      :variant="eligibleVariant"
      @continue="onEligibilityContinue"
      @exit="onEligibilityExit"
      @check-insurance="onEligibilityCheckInsurance"
    />

    <insurance-verification-dialog
      v-if="showInsuranceDialog"
      v-model="showInsuranceDialog"
      :intervention-id="iid ?? undefined"
      @funded="onInsuranceFunded"
      @skipped="onInsuranceSkipped"
      @back="onInsuranceBack"
    />

    <cdc-dpp-fallback-dialog
      v-if="fallbackMode"
      :mode="fallbackMode"
      :granting="grantingYear"
      @grant-year="onFallbackGrantYear"
      @subscriber-continue="onFallbackSubscriberContinue"
      @upgrade="onFallbackUpgrade"
      @decline-to-offer="onFallbackDeclineToOffer"
      @continue-covered="onFallbackContinue"
      @back="onFallbackBack"
      @exit="onFallbackExit"
    />
  </div>
</template>

<style lang="scss" scoped>

.intervention-session-page {
  padding: var(--Spacing-spacing-5);

  @include screen-lg {
    display: flex;
    height: calc(100vh - var(--header-height, 0px));
    max-height: calc(100vh - var(--header-height, 0px));
    overflow: hidden;
    padding-bottom: 0;
  }
}

.toc-container {
  // Padding to accommodate focus ring
  padding-left: 4px;
  padding-top: 4px;
  margin-left: -4px;
  margin-top: -4px;
  margin-bottom: var(--Spacing-spacing-4);

  @include screen-lg {
    margin-bottom: 0;
    @include column-width(2);
    min-width: 250px;
    max-height: 100%;
    padding-right: var(--Spacing-spacing-1);
    padding-bottom: var(--Spacing-spacing-7);
    overflow-y: auto;
    overflow-x: hidden;
    flex-shrink: 0;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.3s;

    &:hover,
    &:active {
      scrollbar-color: var(--Neutral-UI-neutral-ui-40) transparent;
    }
  }
  @include screen-xl {
    padding-right: var(--Spacing-spacing-3);
  }
}

.content-container {
  flex: 1;
  min-width: 0;

  @include screen-lg {
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.3s;

    &:hover,
    &:active {
      scrollbar-color: var(--Neutral-UI-neutral-ui-40) transparent;
    }
  }
}

.content-inner {
  max-width: 1080px;
  margin: 0 auto;
  padding-bottom: var(--Spacing-spacing-9);

  @include screen-md {
    padding-bottom: var(--Spacing-spacing-7);
  }

  @include screen-lg {
    @include page-alignment(1080px);
  }
}

.title-header {
  margin-bottom: var(--Spacing-spacing-6);
}

.homework-section {
  margin-bottom: var(--Spacing-spacing-6);
}

.homework-heading {
  @include font-h3-bold;
  margin-bottom: var(--Spacing-spacing-4);
}

</style>